<script setup lang="ts">
/**
 * Alta pública de cliente desde el QR del mostrador (§21).
 *
 * **Pública**: el token de la URL es la credencial, igual que `accept-invite`.
 * No lleva `publicOnly` — que alguien del centro tenga sesión abierta en el
 * mismo navegador no debería impedirle a un buzo completar el formulario.
 *
 * Una sola columna y sin pasos: se llena de pie, en un teléfono, con el equipo
 * puesto. Un wizard acá sería un formulario largo con más clicks.
 */
import { getPublicIntakeForm, submitPublicIntake, type PublicIntakeForm } from '@/api/publicIntake'
import type { MedicalAnswerValue } from '@/api/medical'
import { ApiError } from '@/api/client'
import AlertBox from '@/components/AlertBox.vue'
import SignaturePad from '@/components/SignaturePad.vue'
import {
  INTAKE_LOCALES,
  detectIntakeLocale,
  intakeStrings,
  pickLabel,
  type IntakeLocale,
} from '@/data/intakeI18n'
import { VNodeRenderer } from '@layouts/components/VNodeRenderer'
import { themeConfig } from '@themeConfig'

definePage({
  meta: { layout: 'blank', public: true },
})

const route = useRoute('intake-token')

const token = computed(() => String(route.params.token || ''))

/*
 * Idioma de la pantalla (§21.8.2).
 *
 * Arranca en el del teléfono y se puede cambiar desde la cabecera. No se
 * persiste: quien abre esto lo abre una vez, desde un QR pegado en un
 * mostrador, y guardar una preferencia para una visita única es guardar por
 * guardar.
 */
const locale = ref<IntakeLocale>(detectIntakeLocale())

const t = computed(() => intakeStrings(locale.value))

const YES_NO = computed(() => [
  { value: true, label: t.value.yes },
  { value: false, label: t.value.no },
] as const)

/**
 * Primera pantalla: la invitación a usar la app.
 *
 * El buzo llega acá escaneando un QR en el mostrador, así que el celular es el
 * caso normal y la app es el mejor lugar para completar esto —queda vinculado y
 * no lo vuelve a tipear en el próximo centro—. Rellenar en el navegador sigue
 * disponible un toque más abajo, sin fricción: quien no quiere instalar nada no
 * puede quedar bloqueado.
 */
const showWelcome = ref(true)

const APP_STORE_URL = 'https://apps.apple.com/us/app/blue-trails/id6753713007'
const PLAY_STORE_URL = 'https://play.google.com/store/apps/details?id=eu.bluetrails'

// Las mismas cinco de bluetrails.eu (el `sameAs` de su structured data).
const SOCIAL_LINKS = [
  { icon: 'ri-linkedin-fill', url: 'https://www.linkedin.com/company/bluetrails/', label: 'LinkedIn' },
  { icon: 'ri-instagram-line', url: 'https://www.instagram.com/bluetrails_official/', label: 'Instagram' },
  { icon: 'ri-twitter-x-fill', url: 'https://x.com/bluetrails_eu', label: 'X' },
  { icon: 'ri-youtube-fill', url: 'https://www.youtube.com/@bluetrails_eu', label: 'YouTube' },
  { icon: 'ri-facebook-fill', url: 'https://www.facebook.com/bluetrailsofficial', label: 'Facebook' },
]

/** Iniciales del centro, para cuando no hay imagen cargada. */
const centerInitials = computed(() =>
  (form.value?.diveCenter.name || '?')
    .split(' ')
    .slice(0, 2)
    .map(w => w.charAt(0).toUpperCase())
    .join(''),
)

const loading = ref(true)
const error = ref('')
const submitting = ref(false)
const done = ref(false)

const form = ref<PublicIntakeForm | null>(null)

const GENERIC_LEVELS = ['Open Water', 'Advanced', 'Rescue', 'Divemaster', 'Instructor']
const BLOOD_TYPES = ['A+', 'A-', 'B+', 'B-', 'AB+', 'AB-', 'O+', 'O-']

/*
 * Agencias y niveles salen del propio payload del formulario. Antes se pedían
 * por `/certification-authorities`, que exige sesión: desde un celular sin
 * sesión devolvía 401 y el portal rebotaba al login del provider — el
 * formulario abría y desaparecía.
 */
const agencyCodes = computed(() => (form.value?.certificationAuthorities ?? []).map(a => a.code))

const levelsOf = (code?: string | null) =>
  (code && form.value?.certificationAuthorities?.find(a => a.code === code)?.levels) || []

const firstName = ref('')
const lastName = ref('')
const email = ref('')
const phone = ref('')
const birthDate = ref('')
const certificationAgency = ref<string | null>(null)
const certificationLevel = ref<string | null>(null)
const loggedDives = ref<number | null>(null)
const emContactName = ref('')
const emContactPhone = ref('')
const emBloodType = ref<string | null>(null)
const emNotes = ref('')

const answers = ref<Record<string, MedicalAnswerValue>>({})
const declaredName = ref('')
const declarationAccepted = ref(false)
const signature = ref('')

const birthDateConfig = { dateFormat: 'Y-m-d', maxDate: 'today' }

const levelItems = computed(() => {
  const own = levelsOf(certificationAgency.value)

  return own.length ? own : GENERIC_LEVELS
})

/*
 * Las preguntas ya resueltas al idioma elegido.
 *
 * El backend manda `labels` con todos los locales cargados, así que cambiar de
 * idioma no vuelve a pedir nada: es un `computed`, no un fetch. Lo que no esté
 * traducido cae al inglés, que es el idioma fuente del catálogo.
 */
const questions = computed(() =>
  (form.value?.questions ?? []).map(q => ({
    ...q,
    text: pickLabel(q.labels, locale.value, q.text),
    helpText: q.helpLabels ? pickLabel(q.helpLabels, locale.value, q.helpText ?? '') : q.helpText,
  })),
)

/*
 * Dos bloques y no una lista larga: el de Blue Trails es el piso que se firma en
 * cualquier centro y el otro es lo que ESTE centro suma. Mezclados, quien
 * contesta no puede saber a quién le está declarando qué — y encima las propias
 * llegaban primero, delante de las diez de siempre.
 *
 * La declaración y la firma siguen siendo UNA sola al pie: es un solo acto de
 * declarar, y partirlo en dos firmas sería pedirle dos veces lo mismo a alguien
 * parado en el mostrador.
 */
const questionBlocks = computed(() =>
  [
    {
      key: 'blue_trails',
      title: t.value.medical,
      subtitle: t.value.medicalSub,
      questions: questions.value.filter(q => q.scope !== 'provider'),
    },
    {
      // Titulado con el nombre del centro: es la forma más directa de decir de
      // quién son estas preguntas, y no necesita traducirse.
      key: 'provider',
      title: form.value?.diveCenter.name || t.value.medical,
      subtitle: t.value.centerQuestionsSub,
      questions: questions.value.filter(q => q.scope === 'provider'),
    },
  ].filter(b => b.questions.length),
)

const unanswered = computed(() =>
  questions.value.filter(q => answers.value[q.code] !== true && answers.value[q.code] !== false),
)

/*
 * Acá **sí** se exigen todas las respuestas, a diferencia del alta que hace el
 * centro: la persona está delante del formulario contestando por sí misma, y
 * una declaración jurada con huecos no declara nada. El backend valida lo mismo.
 */
const canSubmit = computed(
  () =>
    !!firstName.value.trim()
    && !unanswered.value.length
    && !!declaredName.value.trim()
    && declarationAccepted.value
    && !!signature.value,
)

const toggleAnswer = (code: string, value: boolean) => {
  answers.value = {
    ...answers.value,
    [code]: answers.value[code] === value ? null : value,
  }
}

onMounted(async () => {
  try {
    form.value = await getPublicIntakeForm(token.value)
  }
  catch (e) {
    error.value = e instanceof ApiError ? e.message : t.value.notAvailable
  }
  finally {
    loading.value = false
  }
})

async function submit() {
  if (!canSubmit.value)
    return

  error.value = ''
  submitting.value = true
  try {
    await submitPublicIntake(token.value, {
      firstName: firstName.value.trim(),
      lastName: lastName.value.trim() || undefined,
      email: email.value.trim() || undefined,
      phone: phone.value.trim() || undefined,
      birthDate: birthDate.value || undefined,
      certificationLevel: certificationLevel.value ?? undefined,
      certificationAgency: certificationAgency.value ?? undefined,
      loggedDives: loggedDives.value ?? undefined,
      emergency: {
        contactName: emContactName.value.trim() || undefined,
        contactPhone: emContactPhone.value.trim() || undefined,
        bloodType: emBloodType.value ?? undefined,
        notes: emNotes.value.trim() || undefined,
      },
      answers: questions.value.map(q => ({
        questionCode: q.code,
        answer: answers.value[q.code] ?? null,
      })),
      declaredName: declaredName.value.trim(),
      declarationAccepted: true,
      signature: signature.value,
      // El idioma en el que se leyó: es el que queda congelado en cada
      // respuesta de la declaración.
      locale: locale.value,
    })

    done.value = true
  }
  catch (e) {
    error.value = e instanceof ApiError ? e.message : t.value.couldNotSend
  }
  finally {
    submitting.value = false
  }
}
</script>

<template>
  <div class="bt-intake">
    <!--
      Selector de idioma, arriba de todo y siempre visible.

      Es el switcher de la plantilla (`@core/components/I18n.vue`): botón con
      ícono + `VMenu` anclado a él (`location="bottom end"`, offset 15px, ancho
      160) y una `VList` con `mandatory` que marca el idioma activo. Se cambia
      solo el origen del valor —un `ref` propio en vez de `useI18n`, que
      reventaría porque el portal no registra vue-i18n (§18.4)—.

      Antes era un `VSelect` `plain`: su menú se ancla al campo de entrada y no
      al bloque visible, así que el desplegable salía corrido respecto del ícono
      y de la flecha. Un menú anclado a su propio botón no puede desalinearse.

      Sin banderas: un idioma no es un país (mismo criterio que la app). El
      nombre del idioma activo queda a la vista junto al ícono —desvío
      deliberado del original, que es solo ícono—: acá quien mira es un buzo
      cualquiera en un mostrador, no alguien que ya conoce el portal.
    -->
    <div
      v-if="!loading"
      class="d-flex justify-end pt-3"
    >
      <VBtn
        variant="text"
        color="default"
        class="text-none"
        :aria-label="t.language"
      >
        <VIcon
          icon="ri-translate-2"
          start
        />
        {{ INTAKE_LOCALES.find(l => l.code === locale)?.label }}

        <VMenu
          activator="parent"
          location="bottom end"
          offset="15px"
          width="160"
        >
          <VList
            :selected="[locale]"
            color="primary"
            mandatory
          >
            <VListItem
              v-for="lang in INTAKE_LOCALES"
              :key="lang.code"
              :value="lang.code"
              @click="locale = lang.code"
            >
              <VListItemTitle>{{ lang.label }}</VListItemTitle>
            </VListItem>
          </VList>
        </VMenu>
      </VBtn>
    </div>

    <div
      v-if="loading"
      class="d-flex justify-center py-16"
    >
      <VProgressCircular
        indeterminate
        color="primary"
      />
    </div>

    <!-- Acuse final. Sin datos de la ficha: quien la cargó no necesita nada
         más, y el id sería un dato del centro saliendo por un canal público. -->
    <VCard
      v-else-if="done"
      class="text-center pa-8"
    >
      <VIcon
        icon="ri-checkbox-circle-line"
        size="56"
        color="success"
        class="mb-4"
      />
      <h5 class="text-h5 mb-2">
        {{ t.doneTitle }}
      </h5>
      <p class="text-body-1 text-medium-emphasis mb-0">
        {{ t.doneBody(form?.diveCenter.name ?? '') }}
      </p>
    </VCard>

    <VCard
      v-else-if="!form"
      class="pa-8"
    >
      <AlertBox
        variant="danger"
        :message="error"
      />
    </VCard>

    <!--
      Bienvenida: la app primero.

      La cabecera es del DIVE CENTER y va FUERA de la tarjeta, donde antes
      estaba el logo de Blue Trails: la identidad de quien pide los datos va
      sobre el papel, no dentro. Blue Trails firma al pie.
    -->
    <template v-else-if="showWelcome">
      <div class="d-flex flex-column align-center text-center py-6">
        <VAvatar
          size="64"
          rounded
          color="primary"
          variant="tonal"
          class="mb-3"
        >
          <VImg
            v-if="form.diveCenter.logoUrl"
            :src="form.diveCenter.logoUrl"
            cover
          />
          <!-- Sin imagen cargada, las iniciales: un hueco gris no dice de quién
               es este formulario. -->
          <span
            v-else
            class="text-h6"
          >{{ centerInitials }}</span>
        </VAvatar>

        <h5 class="text-h5 mb-0">
          {{ form.diveCenter.name }}
        </h5>
        <span
          v-if="form.diveCenter.city"
          class="text-body-2 text-medium-emphasis"
        >
          {{ form.diveCenter.city }}{{ form.diveCenter.country ? `, ${form.diveCenter.country}` : '' }}
        </span>
      </div>

      <VCard class="pa-2">
        <VCardText>
        <p class="text-body-1 mb-2">
          {{ t.appPitch }}
        </p>
        <p class="text-body-2 text-medium-emphasis mb-6">
          {{ t.appPitchSub }}
        </p>

        <!--
          Mismo tratamiento que bluetrails.eu: pastilla oscura (`btn btn-dark`
          allá) con el SVG de tinta BLANCA adentro. El `*-dark.svg` es la tinta
          oscura para fondos claros y suelto no se parece al badge oficial.
          Anchos 124/139, los mismos de la web.
        -->
        <div class="d-flex flex-wrap justify-center ga-3">
          <a
            :href="APP_STORE_URL"
            target="_blank"
            rel="noopener"
            class="bt-store-btn"
          >
            <img
              src="/images/market/appstore-light.svg"
              alt="Download on the App Store"
              width="124"
            >
          </a>
          <a
            :href="PLAY_STORE_URL"
            target="_blank"
            rel="noopener"
            class="bt-store-btn"
          >
            <img
              src="/images/market/googleplay-light.svg"
              alt="Get it on Google Play"
              width="139"
            >
          </a>
        </div>

        <VDivider class="my-6" />

        <!-- Sin instalar nada: es un botón normal, no letra chica. Quien no
             quiere la app no puede quedar bloqueado en el mostrador. -->
        <div class="text-center">
          <VBtn
            variant="text"
            class="text-none"
            @click="showWelcome = false"
          >
            {{ t.continueHere }}
            <VIcon
              icon="ri-arrow-right-line"
              end
              class="flip-in-rtl"
            />
          </VBtn>
        </div>
        </VCardText>
      </VCard>
    </template>

    <template v-else>
      <VCard class="mb-6">
        <VCardItem>
          <!--
            El logo también acá, no solo en la bienvenida: quien salta directo
            al formulario —o vuelve a él tras cambiar de idioma— tiene que
            seguir viendo de quién es el papel que está por firmar. Es el mismo
            bloque de la bienvenida, en horizontal.
          -->
          <template #prepend>
            <VAvatar
              size="48"
              rounded
              color="primary"
              variant="tonal"
            >
              <VImg
                v-if="form.diveCenter.logoUrl"
                :src="form.diveCenter.logoUrl"
                cover
              />
              <span
                v-else
                class="text-body-1"
              >{{ centerInitials }}</span>
            </VAvatar>
          </template>

          <VCardTitle>{{ form.diveCenter.name }}</VCardTitle>
          <VCardSubtitle v-if="form.diveCenter.city">
            {{ form.diveCenter.city }}{{ form.diveCenter.country ? `, ${form.diveCenter.country}` : '' }}
          </VCardSubtitle>
        </VCardItem>
        <VCardText>
          {{ t.intro }}
        </VCardText>
      </VCard>

      <AlertBox
        variant="danger"
        :message="error"
      />

      <VCard class="mb-6">
        <VCardItem>
          <VCardTitle>{{ t.aboutYou }}</VCardTitle>
        </VCardItem>
        <VCardText>
          <VRow>
            <VCol
              cols="12"
              md="6"
            >
              <VTextField
                v-model="firstName"
                :label="t.firstName"
                :disabled="submitting"
              />
            </VCol>
            <VCol
              cols="12"
              md="6"
            >
              <VTextField
                v-model="lastName"
                :label="t.lastName"
                :disabled="submitting"
              />
            </VCol>
            <VCol
              cols="12"
              md="6"
            >
              <VTextField
                v-model="email"
                :label="t.email"
                type="email"
                :disabled="submitting"
              />
            </VCol>
            <VCol
              cols="12"
              md="6"
            >
              <VTextField
                v-model="phone"
                :label="t.phone"
                :disabled="submitting"
              />
            </VCol>
            <VCol
              cols="12"
              md="6"
            >
              <AppDateTimePicker
                v-model="birthDate"
                :label="t.birthDate"
                class="bt-label-top"
                :config="birthDateConfig"
                :disabled="submitting"
              />
            </VCol>
            <VCol
              cols="12"
              md="6"
            >
              <VTextField
                v-model.number="loggedDives"
                :label="t.loggedDives"
                type="number"
                min="0"
                :disabled="submitting"
              />
            </VCol>
            <VCol
              cols="12"
              md="6"
            >
              <VSelect
                v-model="certificationAgency"
                :items="agencyCodes"
                :label="t.agency"
                clearable
                :disabled="submitting"
              />
            </VCol>
            <VCol
              cols="12"
              md="6"
            >
              <VSelect
                v-model="certificationLevel"
                :items="levelItems"
                :label="t.certificationLevel"
                clearable
                :disabled="submitting"
              />
            </VCol>
          </VRow>
        </VCardText>
      </VCard>

      <VCard class="mb-6">
        <VCardItem>
          <VCardTitle>{{ t.emergencyContact }}</VCardTitle>
          <VCardSubtitle>{{ t.emergencyContactSub }}</VCardSubtitle>
        </VCardItem>
        <VCardText>
          <VRow>
            <VCol
              cols="12"
              md="6"
            >
              <VTextField
                v-model="emContactName"
                :label="t.contactName"
                :disabled="submitting"
              />
            </VCol>
            <VCol
              cols="12"
              md="6"
            >
              <VTextField
                v-model="emContactPhone"
                :label="t.contactPhone"
                :disabled="submitting"
              />
            </VCol>
            <VCol
              cols="12"
              md="6"
            >
              <VSelect
                v-model="emBloodType"
                :items="BLOOD_TYPES"
                :label="t.bloodType"
                clearable
                :disabled="submitting"
              />
            </VCol>
            <VCol cols="12">
              <VTextarea
                v-model="emNotes"
                :label="t.crewNotes"
                rows="2"
                auto-grow
                :disabled="submitting"
              />
            </VCol>
          </VRow>
        </VCardText>
      </VCard>

      <VCard
        v-for="block in questionBlocks"
        :key="block.key"
        class="mb-6"
      >
        <VCardItem>
          <VCardTitle>{{ block.title }}</VCardTitle>
          <VCardSubtitle>
            {{ block.subtitle }}
          </VCardSubtitle>
        </VCardItem>
        <VCardText>
          <div
            v-for="(q, i) in block.questions"
            :key="q.id"
            class="d-flex align-center ga-4 py-3"
            :class="i ? 'border-t' : ''"
          >
            <div class="flex-grow-1">
              <p class="text-body-2 mb-0">
                {{ q.text }}
              </p>
              <p
                v-if="q.helpText"
                class="text-caption text-medium-emphasis mb-0"
              >
                {{ q.helpText }}
              </p>
            </div>

            <VBtnGroup
              class="bt-yes-no flex-0-0"
              density="compact"
              divided
            >
              <VBtn
                v-for="opt in YES_NO"
                :key="String(opt.value)"
                :color="answers[q.code] === opt.value ? 'primary' : undefined"
                :variant="answers[q.code] === opt.value ? 'flat' : 'outlined'"
                :disabled="submitting"
                @click="toggleAnswer(q.code, opt.value)"
              >
                {{ opt.label }}
              </VBtn>
            </VBtnGroup>
          </div>
        </VCardText>
      </VCard>

      <VCard class="mb-6">
        <VCardItem>
          <VCardTitle>{{ t.declaration }}</VCardTitle>
        </VCardItem>
        <VCardText>
          <VCheckbox
            v-model="declarationAccepted"
            :disabled="submitting"
          >
            <template #label>
              <span class="text-body-2">{{ t.declarationText }}</span>
            </template>
          </VCheckbox>

          <VTextField
            v-model="declaredName"
            class="mt-4"
            :label="t.yourFullName"
            :disabled="submitting"
          />

          <p class="text-body-2 mt-6 mb-2">
            {{ t.signature }}
          </p>
          <SignaturePad
            v-model="signature"
            :disabled="submitting"
          />
        </VCardText>
      </VCard>

      <!-- Se dice qué falta en vez de dejar el botón muerto sin explicación:
           en un formulario largo, "no puedo mandarlo" sin motivo es un callejón. -->
      <p
        v-if="!canSubmit"
        class="text-body-2 text-medium-emphasis mb-3"
      >
        <span v-if="unanswered.length">{{ t.questionsLeft(unanswered.length) }}</span>
        <span v-else-if="!firstName.trim()">{{ t.firstNameRequired }}</span>
        <span v-else-if="!declarationAccepted">{{ t.acceptDeclaration }}</span>
        <span v-else-if="!declaredName.trim()">{{ t.typeFullName }}</span>
        <span v-else>{{ t.signAbove }}</span>
      </p>

      <VBtn
        block
        size="large"
        color="primary"
        :loading="submitting"
        :disabled="!canSubmit"
        @click="submit"
      >
        {{ t.sendTo(form.diveCenter.name) }}
      </VBtn>
    </template>

    <!--
      Pie de Blue Trails, con el mismo formato que bluetrails.eu: la plataforma
      firma abajo y la cabecera queda para el dive center, que es con quien el
      buzo está tratando.
    -->
    <div class="text-center mt-10">
      <VNodeRenderer
        :nodes="themeConfig.app.logo"
        class="d-inline-flex mb-3"
      />

      <div class="d-flex justify-center flex-wrap ga-2 mb-3">
        <VBtn
          v-for="s in SOCIAL_LINKS"
          :key="s.label"
          :href="s.url"
          :aria-label="s.label"
          target="_blank"
          rel="noopener"
          icon
          size="small"
          variant="tonal"
          color="primary"
        >
          <VIcon :icon="s.icon" />
        </VBtn>
      </div>

      <p class="text-caption text-medium-emphasis mb-0">
        © Blue Trails GmbH
      </p>
    </div>
  </div>
</template>

<style scoped>
.bt-intake {
  max-inline-size: 720px;
  margin-inline: auto;
  padding-block-end: 48px;
  padding-inline: 16px;
}

/* La pastilla oscura que en la web da `btn btn-dark`. El SVG de adentro es de
   tinta blanca, así que necesita este fondo para leerse. */
.bt-store-btn {
  display: inline-flex;
  align-items: center;
  padding: 8px 16px;
  border-radius: 8px;
  background: #212529;
  transition: opacity 0.2s;
}

.bt-store-btn:hover {
  opacity: 0.85;
}

.bt-store-btn img {
  display: block;
}

/* Ancho fijo en los dos botones: si no, la columna de respuestas queda
   dentada a lo largo de diez preguntas. */
.bt-yes-no :deep(.v-btn) {
  min-inline-size: 68px;
}

</style>
